// ============================================================================
// TAX SERVICE — pack for pickers, preview calculation, schema floor
// ============================================================================
// /api/v1/tax/* of the API template's Tax module (TaxCore ADR-0022). Document
// services on the server compute and persist for real; the web app only
// previews and reads the pack. Nothing here stores anything.

import { get, post, put } from '../client/axiosClient';
import { taxEndpoints } from '../endpoints/tax.endpoints';
import type { ApiResponse } from '../types/common.types';
import type {
  ComponentMixResult,
  EntityTaxJurisdiction,
  PlaceOfSupplyDetermination,
  RegistrationNumberParse,
  TaxPlaceOfSupplyRequest,
  TaxCalculationOutcome,
  TaxCalculationRequest,
  TaxPack,
  TaxJurisdictionOption,
  TaxSchemaFloorStatus,
} from '../types/tax.types';

class TaxService {
  /**
   * The validated pack for a jurisdiction, or the application's default jurisdiction when
   * none is given. Pickers (components, supply types, bases, places of supply, rate codes)
   * read from here so nothing is hard-coded in the web app.
   */
  async getPack(jurisdictionCode?: string): Promise<TaxPack> {
    const res = await get<ApiResponse<TaxPack>>(taxEndpoints.pack(jurisdictionCode));
    return res.data.data;
  }

  /**
   * Preview calculation for invoice / charge-entry forms. A failed calculation comes back as
   * `result.success === false` with named error codes — branch on `errors[].code`, never on
   * message text, and never issue a document from a preview.
   */
  async calculate(request: TaxCalculationRequest): Promise<TaxCalculationOutcome> {
    const res = await post<ApiResponse<TaxCalculationOutcome>>(taxEndpoints.calculate(), request);
    return res.data.data;
  }

  /**
   * What the jurisdiction's published registration-number format makes of a number (ADR-0023
   * §1): well-formed or not, coded problems, and the encoded place — the party's statutory
   * state code. A party form calls this once instead of carrying the regime's layout itself.
   * `asOf` (YYYY-MM-DD) defaults to today on the server.
   */
  async parseRegistrationNumber(number: string, jurisdictionCode?: string, asOf?: string): Promise<RegistrationNumberParse> {
    const res = await get<ApiResponse<RegistrationNumberParse>>(taxEndpoints.registrationParse(number, jurisdictionCode, asOf));
    return res.data.data;
  }

  /**
   * Whether these components may sit together on one document under the jurisdiction's
   * component-set rule (ADR-0023 §3). Refuse when `isAllowed === false`; the wording in
   * `messages` is the engine's.
   */
  async validateComponentMix(componentCodes: string[], jurisdictionCode?: string): Promise<ComponentMixResult> {
    const res = await post<ApiResponse<ComponentMixResult>>(taxEndpoints.componentMix(), { componentCodes, jurisdictionCode });
    return res.data.data;
  }

  /**
   * The place of supply the jurisdiction's rules give for a supply (ADR-0023 §2, engine 2.3.0).
   * Advisory: pre-fill the invoice's place of supply from it and let the user override with a
   * reason; a missing fact or an unpublished rule is a coded problem, not an error.
   */
  async determinePlaceOfSupply(request: TaxPlaceOfSupplyRequest): Promise<PlaceOfSupplyDetermination> {
    const res = await post<ApiResponse<PlaceOfSupplyDetermination>>(taxEndpoints.placeOfSupply(), request);
    return res.data.data;
  }

  /** The per-tenant contract gate; `isReady === false` means tax is blocked until migrations run. */
  /** Legal entities and the jurisdiction each is taxed under (settings cleanup C4). */
  async getEntityJurisdictions(): Promise<EntityTaxJurisdiction[]> {
    const res = await get<ApiResponse<EntityTaxJurisdiction[]>>(taxEndpoints.entityJurisdictions());
    return res.data.data ?? [];
  }

  async getJurisdictionOptions(): Promise<TaxJurisdictionOption[]> {
    const res = await get<ApiResponse<TaxJurisdictionOption[]>>(taxEndpoints.entityJurisdictionOptions());
    return res.data.data ?? [];
  }

  /** Sets (or, with null, clears) an entity's jurisdiction. Audited on the server. */
  /** Saves both: a field left out is cleared on the server. */
  async setEntityJurisdiction(entityId: string, taxJurisdictionCode: string | null, registrationType: string | null): Promise<EntityTaxJurisdiction> {
    const res = await put<ApiResponse<EntityTaxJurisdiction>>(taxEndpoints.entityJurisdiction(entityId), { taxJurisdictionCode, registrationType });
    return res.data.data;
  }

  async getSchemaFloor(): Promise<TaxSchemaFloorStatus> {
    const res = await get<ApiResponse<TaxSchemaFloorStatus>>(taxEndpoints.schemaFloor());
    return res.data.data;
  }
}

export const taxService = new TaxService();
export default taxService;
